<script>
  import { format } from 'svelte-i18n';
  import { Button } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { Input } from '@client/components/input';

  export let values = [];
  export let emptyPlaceholder = '-';

  export let formatKey = (val) => val ?? emptyPlaceholder;
  export let formatValue = (val) => val ?? emptyPlaceholder;

  export let addButtonLabel = $format('label.ADD');
  export let hideActionButtons = false;

  export let validation = undefined;
  export let validationError = undefined;

  export let keyColumnLabel = $format('label.KEY');
  export let keyProperty = 'name';
  export let keyPlaceholder = $format('label.KEY');
  export let keyReadOnly = false;
  export let keyValidation = validation;
  export let keyValidationError = validationError;

  export let valueColumnLabel = $format('label.VALUE');
  export let valueProperty = 'value';
  export let valuePlaceholder = $format('label.VALUE');
  export let valueReadOnly = false;
  export let valueValidation = validation;
  export let valueValidationError = validationError;

  export let editing = false;

  let errors = Array.from({ length: values.length }, () => [false, false]);
  let refs = Array.from({ length: values.length }, () => [null, null]);

  let dragStartIndex = null;
  let dragOverIndex = null;
  let dragFromHandle = false;

  const onDrop = (targetIndex) => {
    if (dragStartIndex === null || dragStartIndex === targetIndex) return;

    const newValues = [...values];
    const [movedValue] = newValues.splice(dragStartIndex, 1);
    newValues.splice(targetIndex, 0, movedValue);
    values = newValues;

    const newErrors = [...errors];
    const [movedErrors] = newErrors.splice(dragStartIndex, 1);
    newErrors.splice(targetIndex, 0, movedErrors);
    errors = newErrors;
  };

  const validateFields = () => {
    let error = false;

    if (values.length && (validation || keyValidation || valueValidation)) {
      values.forEach((item, index) => {
        if (keyValidation && !keyValidation(item[keyProperty])) {
          errors[index][0] = true;
          error = true;
        }

        if (valueValidation && !valueValidation(item[valueProperty])) {
          errors[index][1] = true;
          error = true;
        }
      });

      if (error) {
        return;
      }
    }
  };

  const onAdd = () => {
    values = [...values, { [keyProperty]: '', [valueProperty]: '' }];
    errors = [...errors, [false, false]];
    refs = [...refs, [null, null]];

    setTimeout(() => refs.length && refs.slice(-1)[0][0]?.focus(), 100);
  };

  const onRemove = (index) => {
    values.splice(index, 1);
    values = [...values];

    errors.splice(index, 1);
    errors = [...errors];

    const minIndex = Math.max(index - 1, 0);

    setTimeout(() => refs.length && refs[minIndex][0]?.focus(), 100);
  };

  const onKeyUp = (index, field) => {
    errors[index][field] = false;
    validateFields();
  };
</script>

<div class="field-list">
  {#if editing}
    <slot name="edit" />
    <ul>
      {#if values.length}
        <li class="headings">
          <span class="handle-spacer" aria-hidden="true">&nbsp;</span>
          <span>{keyColumnLabel}</span>
          <span>{valueColumnLabel}</span>
        </li>
      {/if}

      {#each values as item, index}
        <li
          class="item"
          class:dragging={dragStartIndex === index}
          class:drag-over={dragStartIndex !== null && dragOverIndex === index && dragStartIndex !== index}
          draggable={true}
          on:dragstart={(e) => {
            if (!dragFromHandle) {
              e.preventDefault();
              return;
            }
            dragStartIndex = index;
          }}
          on:dragover|preventDefault={() => {
            dragOverIndex = index;
          }}
          on:dragleave={() => {
            if (dragOverIndex === index) dragOverIndex = null;
          }}
          on:drop={() => {
            onDrop(index);
            dragStartIndex = null;
            dragOverIndex = null;
          }}
          on:dragend={() => {
            dragStartIndex = null;
            dragOverIndex = null;
            dragFromHandle = false;
          }}
        >
          <Icon
            name="grip-vertical"
            class="handle"
            on:mousedown={() => (dragFromHandle = true)}
            on:mouseup={() => (dragFromHandle = false)}
          />
          <div class:read-only={keyReadOnly}>
            {#if keyReadOnly}
              {@html formatKey(item[keyProperty] || '&nbsp;')}
            {:else}
              <Input
                type="text"
                class="raw {errors[index][0] ? 'error' : ''}"
                bind:ref={refs[index][0]}
                bind:value={item[keyProperty]}
                on:keyup={() => onKeyUp(index, 0)}
                placeholder={keyPlaceholder}
              />
              {#if errors[index][0]}
                <div class="error">{keyValidationError}</div>
              {/if}
            {/if}
          </div>
          <div class:read-only={valueReadOnly}>
            {#if valueReadOnly}
              {@html formatValue(item[valueProperty] || '&nbsp;')}
            {:else}
              <Input
                type="text"
                class="raw ${errors[index][1] ? 'error' : ''}"
                bind:ref={refs[index][1]}
                bind:value={item[valueProperty]}
                on:keyup={() => onKeyUp(index, 1)}
                placeholder={valuePlaceholder}
              />
            {/if}
          </div>
          {#if !hideActionButtons}
            <Icon name="x-circle" tabindex="0" role="button" on:click={() => onRemove(index)} />
          {/if}
        </li>
        {#if errors[index][0] || errors[index][1]}
          <li class="error">
            <div class="error">{keyValidationError || ''}</div>
            <div class="error">{valueValidationError || ''}</div>
          </li>
        {/if}
      {/each}
    </ul>
    {#if !hideActionButtons}
      <div>
        <Button color="primary" on:click={onAdd}>
          {@html addButtonLabel}
        </Button>
      </div>
    {/if}
  {:else if values.length}
    <div class="field-list">
      <ul>
        <li class="headings">
          <span>{keyColumnLabel}</span>
          <span>{valueColumnLabel}</span>
        </li>
        {#each values as item}
          <li class="item display">
            <div>{@html formatKey(item[keyProperty] || '&nbsp;')}</div>
            <div>{@html formatValue(item[valueProperty] || '&nbsp;')}</div>
          </li>
        {/each}
      </ul>
    </div>
  {:else}
    {emptyPlaceholder}
  {/if}
</div>

<style lang="scss" src="./key-value.scss"></style>
